import { types as sdkTypes } from './sdkLoader';

const { LatLng, LatLngBounds } = sdkTypes;

/** ~meters per degree latitude */
const METERS_PER_DEG_LAT = 111320;

const metersPerDegLng = lat => METERS_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180);

/**
 * Approximate length of the shorter side of a LatLngBounds box, in meters.
 */
export const boundsMinEdgeMetersApprox = bounds => {
  if (!bounds?.ne || !bounds?.sw) {
    return null;
  }
  const { ne, sw } = bounds;
  const latMid = (ne.lat + sw.lat) / 2;
  const latSpanM = Math.abs(ne.lat - sw.lat) * METERS_PER_DEG_LAT;
  const lngSpanM = Math.abs(ne.lng - sw.lng) * metersPerDegLng(latMid);
  return Math.min(latSpanM, lngSpanM);
};

/**
 * Replace bounds with a larger box centered on `origin` when the current box is narrower
 * than `minEdgeMeters` on its shorter side. Uses half-span = `minEdgeMeters` from center
 * in each direction (approx. square whose edge length is ~2 * minEdgeMeters).
 */
export const expandBoundsToMinEdgeMeters = (bounds, origin, minEdgeMeters) => {
  if (!bounds?.ne || !bounds?.sw || origin == null || minEdgeMeters == null || minEdgeMeters <= 0) {
    return bounds;
  }
  const lat = origin.lat;
  const lng = origin.lng;
  if (typeof lat !== 'number' || typeof lng !== 'number') {
    return bounds;
  }
  const halfLat = minEdgeMeters / METERS_PER_DEG_LAT;
  const halfLng = minEdgeMeters / metersPerDegLng(lat);
  return new LatLngBounds(
    new LatLng(lat + halfLat, lng + halfLng),
    new LatLng(lat - halfLat, lng - halfLng)
  );
};

/**
 * Google/Mapbox often return a tight viewport for street addresses (~hundreds of meters).
 * `listings.query` filters by bounds, so nearby listings outside that box never match.
 * When the user has not panned the map (`mapSearch` false), expand tight bounds to at least
 * `standardSearchRadiusMeters` (maps.search.standardSearchRadius) so results match user intent.
 *
 * @param {Object} restOfParams - search params including optional `bounds` and `origin`
 * @param {boolean|undefined} mapSearch - true when URL has mapSearch (user moved map)
 * @param {number|undefined} standardSearchRadiusMeters - from config.maps.search.standardSearchRadius
 * @returns {Object} same shape as restOfParams, possibly with `bounds` replaced
 */
export const maybeExpandTightBoundsForAddressSearch = (
  restOfParams,
  mapSearch,
  standardSearchRadiusMeters
) => {
  if (!restOfParams || mapSearch || standardSearchRadiusMeters == null || standardSearchRadiusMeters <= 0) {
    return restOfParams;
  }
  const { bounds, origin } = restOfParams;
  if (!bounds || !origin) {
    return restOfParams;
  }
  const minEdge = boundsMinEdgeMetersApprox(bounds);
  if (minEdge == null || minEdge >= standardSearchRadiusMeters) {
    return restOfParams;
  }
  const expanded = expandBoundsToMinEdgeMeters(bounds, origin, standardSearchRadiusMeters);
  return { ...restOfParams, bounds: expanded };
};

/**
 * The geocoder puts `bounds` in the URL for map fitting. Flex `listings.query` with `bounds` only
 * returns listings whose geolocation lies inside the box; listings without geo or outside the box
 * match nothing (diag: H6=0, H7>0).
 * Omit `bounds` from the API call when main search is location and `origin` is present, and rely on
 * `origin` (and sort) instead of the viewport rectangle.
 *
 * We do **not** gate on `mapSearch`. The map fires `moveend` after programmatic fitBounds and the
 * URL gets `mapSearch=true` even when the user did not pan; keeping bounds then reproduces H6=0.
 *
 * @param {Object} params - full listings.query param object (incl. bounds, origin, sort, etc.)
 * @param {Object} config - marketplace config (mainSearch.searchType)
 * @returns {Object} params possibly without `bounds`
 */
export const omitBoundsForLocationListingQuery = (params, config) => {
  if (!params || !params.origin || !params.bounds) {
    return params;
  }
  if (config?.search?.mainSearch?.searchType !== 'location') {
    return params;
  }
  const { bounds, ...rest } = params;
  return rest;
};
